import {api} from '../helpers/api';
import {getLangCode} from '../stringBundle';
import {getProvider} from '../helpers/creditHelper';
import {getTrackingValues} from '../helpers/tracking';
import {initializeCreditSDK} from './credit';
import {updateAlertInAlertList} from '../actions/alertList';
import utils from '../helpers/utils';

const CreditModelJSWrapper = () => {
  if (!window.CreditBundle?.com?.gendigital?.credit?.model?.CreditModelJSWrapper) {
    throw new Error('CreditBundle not loaded or CreditModelJSWrapper not available');
  }
  return window.CreditBundle.com.gendigital.credit.model.CreditModelJSWrapper;
};

const CreditModel = () => {
  if (!window.CreditBundle?.com?.gendigital?.credit?.template?.engine?.model) {
    throw new Error('CreditBundle not loaded or CreditModel not available');
  }
  return window.CreditBundle.com.gendigital.credit.template.engine.model;
};

/**
 * Common helper function to ensure Credit SDK is initialized
 * @param dispatch - Redux dispatch function
 * @param getState - Redux getState function
 * @returns Promise that resolves when SDK is initialized or already initialized
 */
async function initializeCreditAlertSDK(dispatch: $TSFixMe, getState: $TSFixMe) {
  const state = getState();
  const auth = state.auth;
  const credit = state.credit;

  const accountId = utils.safeGet(auth, 'user.primaryMember.accountId', '') || '';
  const productFeatures = utils.safeGet(auth, 'user.primaryMember.plan.productFeatures', {});

  // Check specifically for creditMonitoring_1B feature - this is the targeted condition
  const hasCreditMonitoring = utils.has1bOr3bCreditMonitoring(productFeatures);

  if (!hasCreditMonitoring) {
    return;
  }

  const sdkStatus = utils.safeGet(credit, 'init_credit_sdk_status', '');

  if (sdkStatus !== 'success') {
    await initializeCreditSDK({dispatch, accountId});
  }
}

/**
 * Builds logging information for Credit SDK requests
 * @param error - The error that occurred during the request
 * @param startTime - The timestamp when the request started
 * @param creditSdkInterface
 * @param accountId - The account ID associated with the request
 * @param bureau - The credit bureau/provider (optional, e.g., 'EFX', 'TU')
 * @returns An object containing logging information including feature, action type, elapsed time, error details, client ID, and account ID
 */
function buildLogInfo(error: Error, startTime: number, creditSdkInterface: string, accountId: string, bureau?: string) {
  const elapsedTime = Date.now() - startTime;
  return {
    feature: 'Alert-Credit',
    actionType: `${bureau} credit sdk call - ${creditSdkInterface}`,
    elapsedTime,
    error,
    clientId: window.isDwm ? 'dwm' : 'dsp',
    accountId,
  };
}

export const ALERT_DETAIL_REQUEST = 'ALERT_DETAIL_REQUEST';
export const ALERT_DETAIL_RESET = 'ALERT_DETAIL_RESET';
export const ALERT_DETAIL_SUCCESS = 'ALERT_DETAIL_SUCCESS';
export const ALERT_DETAIL_FAILURE = 'ALERT_DETAIL_FAILURE';

export const ALERTS_DETAIL_DISPOSITION_REQUEST = 'ALERTS_DETAIL_DISPOSITION_REQUEST';
export const ALERTS_DETAIL_DISPOSITION_SUCCESS = 'ALERTS_DETAIL_DISPOSITION_SUCCESS';
export const ALERTS_DETAIL_DISPOSITION_FAILURE = 'ALERTS_DETAIL_DISPOSITION_FAILURE';
export const ALERT_DETAIL_SET_DETAIL_VIEW_INDEX = 'ALERT_DETAIL_SET_DETAIL_VIEW_INDEX';

export const ALERT_DETAIL_MOBILE = 'ALERT_DETAIL_MOBILE';

function alertDetailRequest() {
  return {
    type: ALERT_DETAIL_REQUEST,
  };
}

export function alertDetailReset() {
  return {
    type: ALERT_DETAIL_RESET,
  };
}

function alertDetailSuccess(payload: $TSFixMe, options: $TSFixMe) {
  return async(dispatch: $TSFixMe, getState: $TSFixMe) => {
    await initializeCreditAlertSDK(dispatch, getState);
    const payloadUpdated = Object.assign({}, payload);
    const updatedAlertDetail = utils.safeGet(payloadUpdated, 'alertDetail');
    const alertId = utils.safeGet(updatedAlertDetail, 'id');
    if (updatedAlertDetail) {
      const extAlertId = utils.safeGet(updatedAlertDetail, 'extAlertId', '');
      const alertType = utils.safeGet(updatedAlertDetail, 'alertType', '');
      const isEquifaxAlertType = !!(alertType && alertType.indexOf('equifax') >= 0);
      const provider = getProvider('EFX');

      if (extAlertId && isEquifaxAlertType && window.REACT_APP_ENABLE_EFX_ALERTS === true) {
        const startTime = Date.now();
        try {
          const traceId = getTrackingValues().NLokTraceId; // pass along trace id from NGP
          const response = await CreditModelJSWrapper().getAlertDetails(
            extAlertId,
            CreditModel().Provider[provider],
            traceId
          );
          payloadUpdated.alertDetail.efxDetail = JSON.parse(response).creditAlertDetail;

          dispatch({
            type: ALERT_DETAIL_SUCCESS,
            alertDetail: payloadUpdated.alertDetail,
          });

          dispatch(updateAlertInAlertList(alertId, updatedAlertDetail));
        } catch (error: $TSFixMe) {
          utils.log(buildLogInfo(error, startTime, 'Get Alert Detail', extAlertId, provider));
          dispatch({
            type: ALERT_DETAIL_FAILURE,
            error: `Error-${error.message}`,
          });
          return;
        }
      }

      dispatch(updateAlertInAlertList(alertId, updatedAlertDetail));
      dispatch({
        type: ALERT_DETAIL_SUCCESS,
        alertDetail: updatedAlertDetail,
      });
    } else {
      dispatch({
        type: ALERT_DETAIL_FAILURE,
        error: `Unable to load alertId: ${options.alertId}`,
      });
    }
  };
}

function alertDetailFailure(error: $TSFixMe) {
  return (dispatch: $TSFixMe) => {
    dispatch({
      type: ALERT_DETAIL_FAILURE,
      error,
    });
  };
}

export function getAlertDetail(
  alertId: $TSFixMe,
  accountId: $TSFixMe,
  isMobileLogin: $TSFixMe,
  refetch?: $TSFixMeFunction
) {
  const url = utils.getAlertDetailViewPath(alertId);

  const config: FetchConfig = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      account_id: accountId,
      mobileLogin: isMobileLogin,
      lang_code: getLangCode(),
    },
    credentials: 'include',
  };

  const options = {
    alertId,
    refetch,
  };

  return api(url, config, alertDetailRequest(), alertDetailSuccess, alertDetailFailure, options);
}

function alertDetailDispositionRequest() {
  return {
    type: ALERTS_DETAIL_DISPOSITION_REQUEST,
  };
}
function alertDetailDispositionSuccess(payload: $TSFixMe) {
  return (dispatch: $TSFixMe, getState: $TSFixMe) => {
    const previousAlertDetail = getState().alertDetail;
    const previousBucket = utils.safeGet(previousAlertDetail, 'alertDetail.bucket', '');
    const newAlertDetail = utils.safeGet(payload, 'alertDetail');

    if (!newAlertDetail) {
      dispatch({
        type: ALERTS_DETAIL_DISPOSITION_FAILURE,
        error: 'Missing newAlertDetail',
      });
      return;
    }

    newAlertDetail.previousBucket = previousBucket;

    const currentAlertDetail = getState().alertDetail;
    const currentAlertEFXDetail = utils.safeGet(currentAlertDetail, 'alertDetail.efxDetail');
    const dispositionQuestions = utils.safeGet(currentAlertDetail, 'alertDetail.dispositionQuestions', []);

    if (dispositionQuestions) {
      newAlertDetail.dispositionQuestions = dispositionQuestions;
      newAlertDetail.efxDetail = currentAlertEFXDetail;
    }

    dispatch({
      type: ALERTS_DETAIL_DISPOSITION_SUCCESS,
      alertDetail: newAlertDetail,
    });
    dispatch(updateAlertInAlertList(newAlertDetail.id, newAlertDetail));
  };
}

function alertDetailDispositionFailure(error: $TSFixMe) {
  return {
    type: ALERTS_DETAIL_DISPOSITION_FAILURE,
    error,
  };
}

export function setAlertDetailDisposition(options: $TSFixMe, accountId: $TSFixMe, isMobileLogin: $TSFixMe) {
  let dispositionValue = options.disposition;
  if (typeof dispositionValue === 'string') {
    dispositionValue = options.disposition === 'true';
  }
  const url = utils.getAlertDetailPath(options.id);

  const config: FetchConfig = {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': utils.getCookieValue(document.cookie, 'XSRF-TOKEN'),
      account_id: accountId,
      mobileLogin: isMobileLogin,
      lang_code: getLangCode(),
    },
    credentials: 'include',
    body: JSON.stringify({
      disposition: dispositionValue,
    }),
  };

  return api(
    url,
    config,
    alertDetailDispositionRequest(),
    alertDetailDispositionSuccess,
    alertDetailDispositionFailure,
    options
  );
}

export function setAlertDetailViewIndex(index: number): {type: string; index: number} {
  return {
    type: ALERT_DETAIL_SET_DETAIL_VIEW_INDEX,
    index,
  };
}

export function alertDetailMobileMemberChat(alertDetail: $TSFixMe): {type: string; alertDetail: $TSFixMe} {
  return {
    type: ALERT_DETAIL_MOBILE,
    alertDetail,
  };
}
